<template>
  <component
    :is="MdPreviewAsync"
    v-if="ready"
    :key="renderKey"
    :model-value="modelValue"
    v-bind="$attrs"
  />
</template>

<script setup lang="ts">
// md-preview 的按需加载包装层（在 main.ts 中注册为全局组件 MdPreview）
//
// md-editor-v3 在组件 setup() 阶段就快照 config 里的扩展实例，实例晚到不会补渲染，
// 因此必须在创建组件之前把所需扩展注入 config：
//   - katex：公式渲染的必需依赖，由 MdPreviewAsync 的加载器保证（与组件本体并行）
//   - mermaid：体积接近 2MB，只有内容里确实出现 mermaid 代码块时才加载
import { onMounted, ref, watch } from "vue";
import { MdPreviewAsync, preloadMdComponent } from "@/util/md-components";
import { ensureMermaid, isMermaidLoaded } from "@/util/md-extensions";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
});

const ready = ref(false);
const renderKey = ref(0);

// md-editor-v3 只把 info 恰为 mermaid 的围栏识别为流程图，这里保持一致
const MERMAID_FENCE = /(?:^|\n)[ \t]{0,3}(?:`{3,}|~{3,})[ \t]*mermaid[ \t]*(?:\r?\n|$)/i;
const needsMermaid = (md: string) => MERMAID_FENCE.test(md);

const loadMermaid = () =>
  ensureMermaid().catch((e) => {
    console.error("Failed to load mermaid extension:", e);
  });

onMounted(async () => {
  // 只有含流程图的内容才需要等待；此时并行预取组件本体，避免串行等待
  if (needsMermaid(props.modelValue) && !isMermaidLoaded()) {
    await Promise.all([preloadMdComponent(), loadMermaid()]);
  }
  ready.value = true;
});

// 内容后续才出现 mermaid 代码块（如 AI 生成结果）时补加载，并强制重建以使用新实例
watch(
  () => props.modelValue,
  async (md) => {
    if (!ready.value || isMermaidLoaded() || !needsMermaid(md)) {
      return;
    }
    await loadMermaid();
    renderKey.value += 1;
  }
);
</script>
